import { isRouteErrorResponse } from 'react-router';

export type ErrorAudience = 'app' | 'guest';

export type ErrorKind =
  'not-found' | 'forbidden' | 'unauthorized' | 'stale-client' | 'offline' | 'server' | 'unknown';

export type ClassifiedError = {
  kind: ErrorKind;
  status: number;
  title: string;
  description: string;
  detail: string;
  stack?: string;
  digest: string;
};

const COPY: Record<ErrorKind, Record<ErrorAudience, { title: string; description: string }>> = {
  'not-found': {
    app: {
      title: 'This page is not on the menu',
      description: 'The link may be old, or the menu book behind it was unpublished.',
    },
    guest: {
      title: 'This menu is not available',
      description: 'It may have been unpublished. Ask your host for a new link.',
    },
  },
  forbidden: {
    app: {
      title: 'This table is reserved',
      description: 'You do not have access to this page. Ask an owner if you should.',
    },
    guest: {
      title: 'This menu is not available',
      description: 'This book is not open to guests right now. Ask your host.',
    },
  },
  unauthorized: {
    app: {
      title: 'Please sign in again',
      description: 'Your session ended. Sign in to pick up where you left off.',
    },
    guest: {
      title: 'This menu is not available',
      description: 'Please ask your host for a new link.',
    },
  },
  'stale-client': {
    app: {
      title: 'A new version is ready',
      description: 'Refresh to load the latest Menuvera. Takes a second.',
    },
    guest: {
      title: 'Refresh this menu',
      description: 'A newer version of the page is ready. Refresh to open it.',
    },
  },
  offline: {
    app: {
      title: 'We lost the connection',
      description: 'Check the network and try again. Nothing was lost.',
    },
    guest: {
      title: 'Cannot reach the kitchen',
      description: 'Check the connection and try again in a moment.',
    },
  },
  server: {
    app: {
      title: 'The kitchen had a problem',
      description: 'Something failed on our side. Try again in a moment — your menus are safe.',
    },
    guest: {
      title: 'This menu cannot be opened',
      description: 'Please ask your host, or try again in a moment.',
    },
  },
  unknown: {
    app: {
      title: 'Something went off the menu',
      description:
        'An unexpected error stopped this page. Your menus are safe — try again, or head home.',
    },
    guest: {
      title: 'This menu cannot be opened',
      description: 'Please ask your host, or try again in a moment.',
    },
  },
};

export function isGuestPath(pathname: string): boolean {
  return (
    pathname.startsWith('/m/') ||
    pathname.startsWith('/book/') ||
    pathname.startsWith('/t/')
  );
}

export function classifyNotFound(audience: ErrorAudience): ClassifiedError {
  return present('not-found', 404, 'Not Found', audience);
}

export function classifyRouteError(
  error: unknown,
  options: { audience: ErrorAudience },
): ClassifiedError {
  const audience = options.audience;

  if (isRouteErrorResponse(error)) {
    const kind = kindFromStatus(error.status);
    const detail = stringifyData(error.data) || error.statusText || `HTTP ${error.status}`;
    return present(kind, error.status, detail, audience, stackOf(error.data));
  }

  if (isStaleClient(error)) {
    return present('stale-client', 0, messageOf(error), audience, stackOf(error));
  }

  if (isOffline(error)) {
    return present('offline', 0, messageOf(error), audience, stackOf(error));
  }

  if (error instanceof Error) {
    return present('unknown', 500, error.message, audience, error.stack);
  }

  return present('unknown', 500, messageOf(error), audience);
}

function present(
  kind: ErrorKind,
  status: number,
  detail: string,
  audience: ErrorAudience,
  stack?: string,
): ClassifiedError {
  const copy = COPY[kind][audience];
  const resolvedStatus = status > 0 ? status : defaultStatus(kind);
  return {
    kind,
    status: resolvedStatus,
    title: copy.title,
    description: copy.description,
    detail: detail.trim() || copy.title,
    stack: stack?.trim() || undefined,
    digest: digest(`${kind}:${resolvedStatus}:${detail}:${stack ?? ''}`),
  };
}

function kindFromStatus(status: number): ErrorKind {
  if (status === 404) return 'not-found';
  if (status === 403) return 'forbidden';
  if (status === 401) return 'unauthorized';
  if (status >= 500) return 'server';
  if (status >= 400) return 'unknown';
  return 'unknown';
}

function defaultStatus(kind: ErrorKind): number {
  switch (kind) {
    case 'not-found':
      return 404;
    case 'forbidden':
      return 403;
    case 'unauthorized':
      return 401;
    case 'stale-client':
    case 'offline':
      return 0;
    default:
      return 500;
  }
}

function isStaleClient(error: unknown): boolean {
  const message = messageOf(error);
  const name = error instanceof Error ? error.name : '';
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Loading chunk [\w.-]+ failed/i.test(message)
  );
}

function isOffline(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const message = messageOf(error);
  return (
    /Failed to fetch/i.test(message) ||
    /NetworkError/i.test(message) ||
    /Network request failed/i.test(message) ||
    /Load failed/i.test(message)
  );
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error == null) return 'Unknown error';
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function stackOf(error: unknown): string | undefined {
  if (error instanceof Error) return error.stack;
  return undefined;
}

function stringifyData(data: unknown): string {
  if (typeof data === 'string') return data;
  if (data instanceof Error) return data.message;
  if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
    return data.message;
  }
  return '';
}

/** Short, stable handle staff can quote in a support message. */
export function digest(text: string): string {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
